import { User } from "@coworker/apprestructured/src/shared/types/user";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { useTranslation } from "@coworker/locales";
import { StoreTeam } from "@coworker/types/lib/storeteam";
import { useState } from "react";
import { useDeleteTeam, useUpdateTeam } from "../../../hooks/useTeamMutations";
import { NewTeamDataType } from "../../../types/TeamMutationTypes";
import TeamModal from "../TeamModal";
import { TeamDeletionWizard } from "./TeamDeletionWizard/TeamDeletionWizard";

interface EditTeamModalProps {
  visible: boolean;
  close: () => void;
  hasActiveProductQualityTeam: boolean;
  selectedTeam: StoreTeam;
  storeTeams: StoreTeam[] | undefined;
  selectedTeamUsers: User[];
  resetFilters: () => void;
  onDelete: () => void;
}

const EditTeamModal = ({
  visible,
  close,
  hasActiveProductQualityTeam,
  selectedTeam,
  storeTeams,
  selectedTeamUsers,
  resetFilters,
  onDelete,
}: EditTeamModalProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastNotification();

  const { mutateAsync: updateTeam, isPending: isSubmitting } = useUpdateTeam(
    selectedTeam.storeId
  );
  const handleSubmit = (newTeamData: NewTeamDataType) => {
    updateTeam(newTeamData, {
      onSuccess: () => {
        showToast(
          t("teamSuccessfullyUpdatedString", { teamName: newTeamData.name })
        );
        close();
      },
      onError: () => {
        showToast(
          t("failedUpdatingTeamString", { teamName: newTeamData.name })
        );
      },
    });
  };

  const { mutateAsync: deleteTeamAPI, isPending: isDeleting } = useDeleteTeam(
    selectedTeam.storeId
  );
  const deleteSelectedTeam = async () => {
    try {
      await deleteTeamAPI(selectedTeam.id);
      showToast(
        t("teamSuccessfullyDeletedString", { teamName: selectedTeam.name })
      );
      close();
      resetFilters();
      onDelete();
    } catch (error) {
      showToast(t("failedDeletingTeamString", { teamName: selectedTeam.name }));
      throw error;
    }
  };

  const [teamDeletionWizardIsActive, setTeamDeletionWizardIsActive] =
    useState(false);
  const startTeamDeletionWizard = () => setTeamDeletionWizardIsActive(true);
  const endTeamDeletionWizard = () => setTeamDeletionWizardIsActive(false);

  return (
    <>
      <TeamModal
        key={visible ? selectedTeam.id : undefined}
        visible={visible}
        close={close}
        storeId={selectedTeam.storeId}
        hasActiveProductQualityTeam={hasActiveProductQualityTeam}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        initialData={selectedTeam}
        handleDelete={startTeamDeletionWizard}
      />

      {teamDeletionWizardIsActive && (
        <TeamDeletionWizard
          teamToDelete={selectedTeam}
          isDeleting={isDeleting}
          storeTeams={storeTeams}
          teamToDeleteUsers={selectedTeamUsers}
          onDeleteTeam={deleteSelectedTeam}
          onClose={endTeamDeletionWizard}
        />
      )}
    </>
  );
};

export default EditTeamModal;
